import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, BarChart3, Users, MousePointerClick, Globe, Monitor, Sparkles, ChevronRight, CalendarDays, X } from "lucide-react";
import { ru } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { API, CITIES, CITY_NAMES, BUTTON_NAMES, SOURCE_NAMES, AI_SOURCES, DEVICE_ICONS } from "./constants";
import { SourceDrilldown } from "./SourceDrilldown";
import { CITIES as CITY_CONFIG } from "@/cityConfig";

const cityLabel = (slug) => CITY_NAMES[slug] || CITY_CONFIG[slug]?.name || slug;
const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromYmd = (ymd) => { const [y, m, d] = ymd.split("-").map(Number); return new Date(y, m - 1, d); };
const dayLabel = (ymd) => fromYmd(ymd).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
const dayShort = (ymd) => fromYmd(ymd).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });

function DayPicker({ date, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-bold transition-colors ${
              date ? "bg-pink-500 border-pink-500 text-white" : "bg-white border-gray-200 text-gray-700 hover:border-pink-400"
            }`}
            data-testid="analytics-day-picker-btn"
          >
            <CalendarDays className="w-4 h-4" />
            {date ? dayShort(date) : "Выбрать день"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end" data-testid="analytics-day-picker-popover">
          <Calendar
            mode="single"
            locale={ru}
            selected={date ? fromYmd(date) : undefined}
            defaultMonth={date ? fromYmd(date) : new Date()}
            disabled={{ after: new Date() }}
            onSelect={(d) => { if (d) { onChange(toYmd(d)); setOpen(false); } }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {date && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          title="Сбросить день"
          data-testid="analytics-day-picker-clear"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "pink" }) {
  const colors = {
    pink: "bg-pink-50 text-pink-600",
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`stat-${label}`}>
      <div className={`inline-flex p-2.5 rounded-xl ${colors[color]} mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</div>
      <div className="text-xs text-gray-500 mt-1 font-medium">{label}</div>
    </div>
  );
}

export function AnalyticsTab({ password }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");
  const [date, setDate] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [selectedSource, setSelectedSource] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const params = { password, period };
        if (date) params.date = date;
        if (filterCity) params.city = filterCity;
        const res = await axios.get(`${API}/admin/analytics`, { params });
        setData(res.data);
      } catch {
        toast.error("Ошибка загрузки аналитики");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [password, period, date, filterCity]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Загрузка аналитики...</p>
      </div>
    );
  }

  const hasData = data.total_views > 0 || data.total_clicks > 0;
  const conversionRate = data.total_views > 0 ? ((data.total_clicks / data.total_views) * 100).toFixed(1) : "0.0";

  const periods = [
    { value: "today", label: "Сегодня" },
    { value: "7d", label: "7 дней" },
    { value: "30d", label: "30 дней" },
    { value: "90d", label: "90 дней" },
  ];

  return (
    <div data-testid="analytics-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Аналитика</h1>
          <p className="text-gray-500 text-sm">
            Статистика посещений и кликов
            {date && <span className="text-pink-500 font-semibold" data-testid="analytics-selected-day"> — {dayLabel(date)} (UTC)</span>}
            {filterCity && <span className="text-pink-500 font-semibold"> — {CITY_NAMES[filterCity] || filterCity}</span>}
          </p>
        </div>
        <div className="flex flex-wrap lg:flex-nowrap items-center gap-3">
          <DayPicker date={date} onChange={setDate} />
          <select
            value={filterCity}
            onChange={e => setFilterCity(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium bg-white focus:border-pink-400 outline-none"
            data-testid="analytics-city-filter"
          >
            <option value="">Все города</option>
            {CITIES.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {periods.map(p => (
              <button
                key={p.value}
                onClick={() => { setDate(""); setPeriod(p.value); }}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
                  !date && period === p.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Eye} label="Просмотры" value={data.total_views} color="blue" />
        <StatCard icon={Users} label="Уникальные" value={data.unique_visitors} color="green" />
        <StatCard icon={MousePointerClick} label="Клики" value={data.total_clicks} color="pink" />
        <StatCard icon={Globe} label="Конверсия" value={`${conversionRate}%`} color="amber" />
      </div>

      {/* Chart */}
      {data.chart && data.chart.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-8" data-testid="analytics-chart">
          <h3 className="font-bold text-gray-900 mb-4">{date ? `Просмотры и клики по часам — ${dayLabel(date)} (UTC)` : "Просмотры и клики по дням"}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => (date ? v : v.slice(5))} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                labelFormatter={v => (date ? `${v} UTC` : new Date(v).toLocaleDateString("ru-RU", { day: "numeric", month: "long" }))}
              />
              <Area type="monotone" dataKey="views" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} name="Просмотры" />
              <Area type="monotone" dataKey="clicks" stackId="2" stroke="#ec4899" fill="#ec4899" fillOpacity={0.2} name="Клики" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Grid: Cities, Sources, Devices, Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        {/* Popular cities */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-cities">
          <h3 className="font-bold text-gray-900 mb-4">Популярные города</h3>
          {data.cities.length === 0 ? (
            <p className="text-sm text-gray-400">Нет данных</p>
          ) : (
            <div className="space-y-3">
              {data.cities.map(c => {
                const maxCount = data.cities[0]?.count || 1;
                return (
                  <div key={c.city}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{cityLabel(c.city)}</span>
                      <span className="text-sm font-bold text-gray-900">{c.count}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-pink-400 to-rose-500 rounded-full transition-all" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Traffic sources */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-sources">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Источники трафика</h3>
            <span className="text-[11px] text-gray-400">нажмите на источник — откроется детализация</span>
          </div>
          {data.sources.length === 0 ? (
            <p className="text-sm text-gray-400">Нет данных</p>
          ) : (
            <div className="space-y-1">
              {data.sources.map(s => {
                const maxCount = data.sources[0]?.count || 1;
                const isActive = selectedSource === s.source;
                const isAi = AI_SOURCES.includes(s.source);
                return (
                  <button
                    key={s.source}
                    type="button"
                    onClick={() => setSelectedSource(isActive ? null : s.source)}
                    className={`w-full text-left rounded-xl px-2 py-2 -mx-2 transition-colors ${isActive ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                    data-testid={`source-row-${s.source}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium inline-flex items-center gap-1.5 ${isActive ? "text-indigo-700" : "text-gray-700"}`}>
                        {SOURCE_NAMES[s.source] || s.source}
                        {isAi && <Sparkles className="w-3.5 h-3.5 text-violet-500" />}
                      </span>
                      <span className="text-sm font-bold text-gray-900 inline-flex items-center gap-1.5">
                        {s.count}
                        <ChevronRight className={`w-3.5 h-3.5 text-gray-300 transition-transform ${isActive ? "rotate-90 text-indigo-500" : ""}`} />
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isAi ? "bg-gradient-to-r from-violet-400 to-indigo-500" : "bg-gradient-to-r from-blue-400 to-blue-500"}`} style={{ width: `${(s.count / maxCount) * 100}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Source drill-down — right under the sources card */}
        {selectedSource && (
          <div className="sm:col-span-2">
            <SourceDrilldown password={password} source={selectedSource} period={period} date={date} city={filterCity} onClose={() => setSelectedSource(null)} />
          </div>
        )}

        {/* Button clicks */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-buttons">
          <h3 className="font-bold text-gray-900 mb-4">Клики по кнопкам</h3>
          {data.buttons.length === 0 ? (
            <p className="text-sm text-gray-400">Нет данных</p>
          ) : (
            <div className="space-y-3">
              {data.buttons.map(b => (
                <div key={b.button} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-700">{BUTTON_NAMES[b.button] || b.button}</span>
                  <span className="text-lg font-bold text-gray-900">{b.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-devices">
          <h3 className="font-bold text-gray-900 mb-4">Устройства</h3>
          {data.devices.length === 0 ? (
            <p className="text-sm text-gray-400">Нет данных</p>
          ) : (
            <div className="space-y-3">
              {data.devices.map(d => {
                const DeviceIcon = DEVICE_ICONS[d.device] || Monitor;
                const names = { desktop: "Компьютер", mobile: "Мобильный", tablet: "Планшет" };
                return (
                  <div key={d.device} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <DeviceIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">{names[d.device] || d.device}</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900">{d.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp / Telegram clicks per city */}
      <ClickCitiesCard rows={data.click_cities || []} />

      {/* Top pages */}
      {data.pages && data.pages.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-pages">
          <h3 className="font-bold text-gray-900 mb-4">Топ страниц</h3>
          <div className="space-y-2">
            {data.pages.map((p, i) => (
              <div key={p.page} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-b-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <span className="text-sm font-medium text-gray-700 font-mono">{p.page}</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClickCitiesCard({ rows }) {
  const totals = rows.reduce((a, r) => ({ wa: a.wa + r.whatsapp, tg: a.tg + r.telegram }), { wa: 0, tg: 0 });
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="analytics-click-cities">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="font-bold text-gray-900 inline-flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-pink-500" />Клики WhatsApp / Telegram по городам</h3>
        <span className="text-xs text-gray-500" data-testid="click-cities-totals">WhatsApp {totals.wa} · Telegram {totals.tg}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400" data-testid="click-cities-empty">Нет данных за период</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="click-cities-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-3 font-semibold">Город</th>
                <th className="py-2 px-3 font-semibold text-right">WhatsApp</th>
                <th className="py-2 px-3 font-semibold text-right">Telegram</th>
                <th className="py-2 pl-3 font-semibold text-right">Всего</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.city} className="border-b border-gray-50 last:border-0" data-testid={`click-city-row-${r.city}`}>
                  <td className="py-2 pr-3 font-medium text-gray-800">{cityLabel(r.city)}</td>
                  <td className="py-2 px-3 text-right text-[#128c7e] font-semibold">{r.whatsapp}</td>
                  <td className="py-2 px-3 text-right text-[#0088cc] font-semibold">{r.telegram}</td>
                  <td className="py-2 pl-3 text-right font-bold text-gray-900">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
